import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { productsApi } from '../api/products.api';
import type { Product, ProductCardResponse, CreateProductDto, SetDiscountDto, FlowerType, AssemblyType, Color, SellerPromotionListResponse, SellerSearchPreviewResponse, SellerCatalogCityOption, UpdateProductPromotionDto } from '../types';

export interface ProductFilters {
  categoryId?: number;
  colorIds?: number[];
  flowerTypeIds?: number[];
  assemblyTypeIds?: number[];
  minPrice?: number;
  maxPrice?: number;
  sortBy?: string;
}

export const useProducts = (filters?: ProductFilters) => {
  return useQuery<ProductCardResponse[]>({
    queryKey: ['products', 'list', filters],
    queryFn: () => productsApi.getAll(filters),
  });
};

export const useFlowerTypes = () => {
  return useQuery<FlowerType[]>({
    queryKey: ['products', 'options', 'flower-types'],
    queryFn: () => productsApi.getFlowerTypes(),
  });
};

export const useAssemblyTypes = () => {
  return useQuery<AssemblyType[]>({
    queryKey: ['products', 'options', 'assembly-types'],
    queryFn: () => productsApi.getAssemblyTypes(),
  });
};

export const useColors = () => {
  return useQuery<Color[]>({
    queryKey: ['products', 'options', 'colors'],
    queryFn: () => productsApi.getColors(),
  });
};

/** Категории, в которых у текущего продавца есть хотя бы один товар (для фильтра списка товаров). */
export const useSellerProductCategories = (enabled = true) => {
  return useQuery<Array<{ id: number; name: string; slug: string }>>({
    queryKey: ['products', 'seller-categories'],
    queryFn: () => productsApi.getSellerCategories(),
    enabled,
  });
};

// Хуки для получения готовых опций для селектов
export const useFlowerTypeOptions = () => {
  const { data: flowerTypes = [] } = useFlowerTypes();

  return useMemo(() => {
    return flowerTypes.map(ft => ({
      value: ft.id.toString(),
      label: ft.name,
    }));
  }, [flowerTypes]);
};

export const useAssemblyTypeOptions = () => {
  const { data: assemblyTypes = [] } = useAssemblyTypes();

  return useMemo(() => {
    return assemblyTypes.map(at => ({
      value: at.id.toString(),
      label: at.name,
    }));
  }, [assemblyTypes]);
};

export const useColorOptions = () => {
  const { data: colors = [] } = useColors();

  return useMemo(() => {
    return colors.map(c => ({
      value: c.id.toString(),
      label: c.name,
    }));
  }, [colors]);
};

export const useProduct = (id: number) => {
  return useQuery<Product>({
    queryKey: ['product', id],
    queryFn: () => productsApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation<Product, Error, { data: CreateProductDto; files?: File[] }>({
    mutationFn: ({ data, files }) => productsApi.create(data, files),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'seller-categories'] });
      // Если создается вариант товара, инвалидируем продукт, от которого создавали вариант
      if (variables.data.variantInfo?.variantOfProductId) {
        queryClient.invalidateQueries({ queryKey: ['product', variables.data.variantInfo.variantOfProductId] });
      }
    },
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation<Product, Error, { id: number; data: Partial<CreateProductDto>; files?: File[]; deletedImageIds?: number[] }>({
    mutationFn: ({ id, data, files, deletedImageIds }) => productsApi.update(id, data, files, deletedImageIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'seller-categories'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
    },
  });
};

export const useReorderProductImages = (productId: number) => {
  const queryClient = useQueryClient();

  return useMutation<Product, Error, { id: number; order: number }[]>({
    mutationFn: (items) => productsApi.updateProductImagesOrder(productId, items),
    onSuccess: (data) => {
      queryClient.setQueryData(['product', productId], (prev: Product | undefined) =>
        prev ? { ...prev, images: data.images } : prev,
      );
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось изменить порядок изображений',
        color: 'red',
      });
    },
  });
};

export const useSetProductDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation<Product, Error, { id: number; data: SetDiscountDto }>({
    mutationFn: ({ id, data }) => productsApi.setDiscount(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections', 'discount'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
    },
  });
};

export const useSetProductFlag = (type: string) => {
  const queryClient = useQueryClient();

  return useMutation<Product, Error, { id: number; value: boolean }>({
    mutationFn: ({ id, value }) => productsApi.setFlag(id, type, value),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections', type] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
    },
  });
};


export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => productsApi.delete(id),
    onSuccess: (_, id) => {
      // Инвалидируем все списки продуктов
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'seller-categories'] });
      // Инвалидируем все коллекции продуктов
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
      // Инвалидируем список подборок для обновления информации о подборках
      queryClient.invalidateQueries({ queryKey: ['customSets'] });
      // Удаляем кэш для удаленного товара
      queryClient.removeQueries({ queryKey: ['product', id] });
      // Примечание: конкретные подборки инвалидируются в компоненте на основе данных товара
    },
  });
};

export const usePopularProducts = () => {
  return useQuery<ProductCardResponse[]>({
    queryKey: ['products', 'collections', 'popular'],
    queryFn: () => productsApi.getPopular(),
  });
};

export const useHiddenProducts = () => {
  return useQuery<ProductCardResponse[]>({
    queryKey: ['products', 'collections', 'hidden'],
    queryFn: () => productsApi.getHidden(),
  });
};

export const useDiscountProducts = () => {
  return useQuery<ProductCardResponse[]>({
    queryKey: ['products', 'collections', 'discount'],
    queryFn: () => productsApi.getDiscount(),
  });
};

export const useOutOfStockProducts = () => {
  return useQuery<ProductCardResponse[]>({
    queryKey: ['products', 'collections', 'out-of-stock'],
    queryFn: () => productsApi.getOutOfStock(),
  });
};

export const useNewProducts = () => {
  return useQuery<ProductCardResponse[]>({
    queryKey: ['products', 'collections', 'new'],
    queryFn: () => productsApi.getNew(),
  });
};

export const useCollectionsCounts = () => {
  return useQuery<{
    popular: number;
    hidden: number;
    discount: number;
    outOfStock: number;
    new: number;
  }>({
    queryKey: ['products', 'collections', 'counts'],
    queryFn: () => productsApi.getCollectionsCounts(),
  });
};

export const useCollectionProducts = (type: string) => {
  return useQuery<ProductCardResponse[]>({
    queryKey: ['products', 'collections', type],
    queryFn: () => productsApi.getCollection(type),
    enabled: !!type,
  });
};

// Массовые операции
export const useBulkSetFlag = (type: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ count: number }, Error, { productIds: number[]; value: boolean }>({
    mutationFn: ({ productIds, value }) => productsApi.bulkSetFlag(type, productIds, value),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections', type] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
      // Инвалидируем кэш для каждого обновленного продукта
      variables.productIds.forEach(productId => {
        queryClient.invalidateQueries({ queryKey: ['product', productId] });
      });
    },
  });
};


export const useBulkSetCollection = (type: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ count: number }, Error, { productIds: number[]; value: boolean }>({
    mutationFn: ({ productIds, value }) => productsApi.bulkSetFlag(type, productIds, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections', type] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
    },
  });
};

export const useBulkDeleteProducts = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { productIds: number[] }>({
    mutationFn: ({ productIds }) => productsApi.bulkDelete(productIds),
    onSuccess: (_, variables) => {
      // Инвалидируем все списки продуктов
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'seller-categories'] });
      // Инвалидируем все коллекции продуктов
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
      // Инвалидируем все кастомные подборки (так как мы не знаем, в каких именно были товары)
      queryClient.invalidateQueries({ queryKey: ['customSetProducts'] });
      queryClient.invalidateQueries({ queryKey: ['customSet'] });
      // Инвалидируем список подборок для обновления информации о подборках
      queryClient.invalidateQueries({ queryKey: ['customSets'] });
      // Удаляем кеш для удаленных товаров
      variables.productIds.forEach(id => {
        queryClient.removeQueries({ queryKey: ['product', id] });
      });
    },
  });
};

export const useBulkAddToCategory = () => {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; count: number }, Error, { productIds: number[]; categoryId: number }>({
    mutationFn: ({ productIds, categoryId }) => productsApi.bulkAddToCategory(productIds, categoryId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'seller-categories'] });
      // Инвалидируем кеш для обновленных товаров
      variables.productIds.forEach(id => {
        queryClient.invalidateQueries({ queryKey: ['product', id] });
      });
    },
  });
};

export const useRemoveFromCategory = () => {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { productId: number; categoryId: number }>({
    mutationFn: ({ productId, categoryId }) => productsApi.removeFromCategory(productId, categoryId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'seller-categories'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.productId] });
    },
  });
};

// Color mutations
export const useCreateColor = () => {
  const queryClient = useQueryClient();

  return useMutation<Color, Error, { name: string; hexCode?: string }>({
    mutationFn: (data) => productsApi.createColor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'colors'] });
    },
  });
};

export const useUpdateColor = () => {
  const queryClient = useQueryClient();

  return useMutation<Color, Error, { id: number; data: { name?: string; hexCode?: string } }>({
    mutationFn: ({ id, data }) => productsApi.updateColor(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'colors'] });
    },
  });
};

export const useDeleteColor = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => productsApi.deleteColor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'colors'] });
    },
  });
};

// FlowerType mutations
export const useCreateFlowerType = () => {
  const queryClient = useQueryClient();

  return useMutation<FlowerType, Error, { name: string }>({
    mutationFn: (data) => productsApi.createFlowerType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'flower-types'] });
    },
  });
};

export const useUpdateFlowerType = () => {
  const queryClient = useQueryClient();

  return useMutation<FlowerType, Error, { id: number; data: { name?: string } }>({
    mutationFn: ({ id, data }) => productsApi.updateFlowerType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'flower-types'] });
    },
  });
};

export const useDeleteFlowerType = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => productsApi.deleteFlowerType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'flower-types'] });
    },
  });
};

// AssemblyType mutations
export const useCreateAssemblyType = () => {
  const queryClient = useQueryClient();

  return useMutation<AssemblyType, Error, { name: string }>({
    mutationFn: (data) => productsApi.createAssemblyType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'assembly-types'] });
    },
  });
};

export const useUpdateAssemblyType = () => {
  const queryClient = useQueryClient();

  return useMutation<AssemblyType, Error, { id: number; data: { name?: string } }>({
    mutationFn: ({ id, data }) => productsApi.updateAssemblyType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'assembly-types'] });
    },
  });
};

export const useDeleteAssemblyType = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => productsApi.deleteAssemblyType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'assembly-types'] });
    },
  });
};

export const useCreateVariant = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; variantId: number; message: string },
    Error,
    { products: Array<{ productId: number }> }
  >({
    mutationFn: (data) => productsApi.createVariant(data),
    onSuccess: (_, variables) => {
      // Инвалидируем кэш для всех товаров в варианте
      variables.products.forEach(product => {
        queryClient.invalidateQueries({ queryKey: ['product', product.productId] });
      });
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
};

export const useUpdateVariantName = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; message: string },
    Error,
    { productId: number; variantName: string }
  >({
    mutationFn: ({ productId, variantName }) => productsApi.updateVariantName(productId, variantName),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product', variables.productId] });
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
};

export const useBulkUpdateVariantName = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; count: number; total: number; results: Array<{ productId: number; success: boolean; message: string }>; message: string },
    Error,
    { updates: Array<{ productId: number; variantName: string }> }
  >({
    mutationFn: ({ updates }) => productsApi.bulkUpdateVariantName(updates),
    onSuccess: (_, variables) => {
      // Инвалидируем кэш для всех обновленных товаров
      variables.updates.forEach(update => {
        queryClient.invalidateQueries({ queryKey: ['product', update.productId] });
      });
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
};

export const useRemoveVariant = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; message: string },
    Error,
    { variantId: number; productId: number }
  >({
    mutationFn: ({ variantId }) => productsApi.removeVariant(variantId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product', variables.productId] });
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
};

export const useDisbandVariantGroup = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; message: string },
    Error,
    number
  >({
    mutationFn: (productId) => productsApi.disbandVariantGroup(productId),
    onSuccess: (_, productId) => {
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
};

export const SELLER_PROMOTION_QUERY_KEY = ['products', 'seller-promotion'] as const;

export const useSellerPromotion = () => {
  const queryClient = useQueryClient();
  const list = useQuery<SellerPromotionListResponse>({
    queryKey: SELLER_PROMOTION_QUERY_KEY,
    queryFn: () => productsApi.getSellerPromotion(),
  });
  const hasPending = list.data?.items.some((item) => item.rankPending) ?? false;
  const pending = useQuery({
    queryKey: ['products', 'seller-promotion-rank-pending'],
    queryFn: () => productsApi.getSellerPromotionRankPending(),
    enabled: hasPending,
    refetchInterval: hasPending ? 4000 : false,
  });

  useEffect(() => {
    if (!pending.data) return;
    const pendingIds = new Set(pending.data.productIds);
    queryClient.setQueryData<SellerPromotionListResponse>(
      SELLER_PROMOTION_QUERY_KEY,
      (current) => {
        if (!current) return current;
        let changed = false;
        const items = current.items.map((item) => {
          const next = pendingIds.has(item.productId);
          if (item.rankPending === next) return item;
          changed = true;
          return { ...item, rankPending: next };
        });
        return changed ? { ...current, items } : current;
      },
    );
  }, [pending.data, queryClient]);

  return list;
};

export const SELLER_PROMOTION_CITIES_QUERY_KEY = [
  'products',
  'seller-promotion-cities',
] as const;

export const useSellerPromotionCities = () => {
  return useQuery<{ cities: SellerCatalogCityOption[] }>({
    queryKey: SELLER_PROMOTION_CITIES_QUERY_KEY,
    queryFn: () => productsApi.getSellerPromotionCities(),
    staleTime: 60_000,
  });
};

export const SELLER_SEARCH_PREVIEW_QUERY_KEY = [
  'products',
  'seller-search-preview',
] as const;

export const useSellerSearchPreview = (params: {
  keyword?: string;
  categoryId?: number | null;
  cityKey?: string | null;
  enabled?: boolean;
}) => {
  const keyword = params.keyword?.trim() ?? '';
  return useQuery<SellerSearchPreviewResponse>({
    queryKey: [
      ...SELLER_SEARCH_PREVIEW_QUERY_KEY,
      keyword,
      params.categoryId ?? null,
      params.cityKey ?? null,
    ],
    queryFn: () =>
      productsApi.getSellerSearchPreview({
        keyword: keyword || undefined,
        categoryId: params.categoryId ?? undefined,
        cityKey: params.cityKey ?? undefined,
      }),
    enabled: params.enabled !== false,
  });
};

export const useUpdateSellerPromotion = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { ok: boolean },
    Error,
    { id: number; data: UpdateProductPromotionDto }
  >({
    mutationFn: ({ id, data }) => productsApi.updatePromotion(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: SELLER_PROMOTION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['seller-advertising-credit'] });
    },
  });
};

